<template>
    <div class="flex items-center relative" :class="tabHeightClassName" v-if="variant === 'card'">
        <div
            class="scroll-hidden flex relative whitespace-no-wrap mr-2 overflow-x-auto | transition-all duration-200"
            :class="tabHeightClassName"
            ref="scrollBar"
        >
            <div
                class="bottom-0 rounded-t-lg shadow-lg absolute bg-white pointer-events-none"
                :class="[
                    tabWidthClassName,
                    tabHeightClassName,
                    { 'transition-all duration-200 ease-in-out': mounted },
                ]"
                :style="{ left: backgroundPosition }"
                ref="tabBackground"
            />
            <activix-tab
                :class="[tabWidthClassName, tabHeightClassName]"
                :hide-separator="hideSeparator(tab)"
                :is-active="activeTab === tab.name"
                :is-disabled="tab.disabled"
                :text-alignment="textAlignment"
                :label="tab.label"
                :sub-label="tab.subLabel"
                :tooltip="tab.tooltip"
                :key="tab.name"
                @click="onTabClick(tab)"
                v-for="tab in tabs"
            >
                <template #moreButtons>
                    <slot :active-tab="activeTab" name="moreButtons" />
                </template>
            </activix-tab>
            <div class="flex items-center px-6" :class="tabHeightClassName" v-if="hasAfterSlot">
                <slot name="after" />
            </div>
        </div>
    </div>

    <div class="flex items-center gap-6" v-else>
        <button
            class="relative shrink-0 pt-2.5 pb-1.5 text-sm font-semibold border-b-[3px] transition-colors duration-200 focus:outline-none"
            :class="underlineTabClassName(tab)"
            type="button"
            :disabled="tab.disabled"
            :key="tab.name"
            @click="onTabClick(tab)"
            v-tooltip="tab.tooltip"
            v-for="tab in tabs"
        >
            {{ tab.label }}
        </button>
        <slot name="after" />
    </div>
</template>

<script lang="ts" setup>
    import {
        computed,
        getCurrentInstance,
        nextTick,
        onMounted,
        ref,
        useSlots,
        watch,
        watchEffect,
    } from 'vue';
    import ActivixTab from '@/components/activix-ui/ActivixTabs/ActivixTab.vue';
    import type {
        ActivixTabItem,
        TabAlignment,
        TabVariant,
    } from '@/components/activix-ui/ActivixTabs/types.ts';

    defineOptions({
        compatConfig: {
            COMPONENT_V_MODEL: false,
        },
    });

    const props = withDefaults(
        defineProps<{
            tabs: ActivixTabItem[];
            modelValue: string | null;
            tabWidthClassName?: string;
            textAlignment?: TabAlignment;
            variant?: TabVariant;
        }>(),
        {
            tabWidthClassName: 'w-40',
            textAlignment: 'center',
            variant: 'card',
        }
    );

    const emit = defineEmits<(event: 'update:modelValue', value: string | null) => void>();

    const slots = useSlots();
    const hasAfterSlot = computed(() => !!slots.after);

    const activeTab = ref<string | null>(props.modelValue);
    const backgroundPosition = ref<string>('0px');
    const mounted = ref(false);

    const tabBackground = ref<HTMLElement | null>(null);
    const instance = getCurrentInstance();

    const hasSubLabel = computed(() => props.tabs.some((tab) => !!tab.subLabel));

    const tabHeightClassName = computed(() => {
        return hasSubLabel.value ? 'h-16' : 'h-12';
    });

    const currentTabIndex = computed(() => {
        return props.tabs.findIndex((tab) => tab.name === activeTab.value);
    });

    const hideSeparator = (tab: ActivixTabItem) => {
        const index = props.tabs.findIndex((t) => t.name === tab.name);

        if (index + 1 === currentTabIndex.value) {
            return true;
        }

        return index === props.tabs.length - 1 && !hasAfterSlot.value;
    };

    const underlineTabClassName = (tab: ActivixTabItem) => {
        if (tab.disabled) {
            return 'cursor-not-allowed text-gray-400 border-transparent';
        }

        if (tab.name === activeTab.value) {
            return 'text-blue-600 border-blue-600';
        }

        return 'text-gray-600 border-transparent hover:text-gray-700';
    };

    const setBackgroundPosition = () => {
        const tabElement =
            tabBackground.value || (instance?.proxy?.$refs.tabBackground as HTMLElement | null);

        if (!tabElement || currentTabIndex.value < 0) {
            backgroundPosition.value = '0px';
            return;
        }

        backgroundPosition.value = `${currentTabIndex.value * tabElement.offsetWidth}px`;
    };

    const resetActiveTab = () => {
        activeTab.value = props.tabs[0]?.name ?? null;
    };

    const onTabClick = (tab: ActivixTabItem) => {
        if (tab.disabled) {
            return;
        }

        activeTab.value = tab.name;
    };

    watch(
        () => props.modelValue,
        (value) => {
            if (activeTab.value !== value) {
                activeTab.value = value;
            }
        }
    );

    const lastEmittedActiveTab = ref<string | null>(activeTab.value ?? null);

    watchEffect(() => {
        if (currentTabIndex.value === -1) {
            resetActiveTab();
        }

        setBackgroundPosition();

        if (activeTab.value !== lastEmittedActiveTab.value) {
            lastEmittedActiveTab.value = activeTab.value;
            emit('update:modelValue', activeTab.value);
        }
    });

    onMounted(async () => {
        if (!props.modelValue) {
            resetActiveTab();
        }

        setBackgroundPosition();

        await nextTick();
        mounted.value = true;
    });
</script>

<style scoped>
    .scroll-hidden::-webkit-scrollbar {
        /* Note: scrollbar styles are not supported in tailwind, we have to specify them manually */
        display: none;
        -ms-overflow-style: none;
        scrollbar-width: none;
    }
</style>
